import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export interface TechnicalSignal {
    date: string;
    type: string;
    detail: string | null;
    severity: string | null;
    indicator_value: number | null;
}

export interface ConvergenceStock {
    symbole: string;
    nom_societe: string;
    cours_actuel: number;
    date_calcul: string | null;
    potentiel_pct: number;
    recommendation: string | null;
    fiabilite: string | null;
    tech_score: number;
    nb_signals: number;
    signals: TechnicalSignal[];
    is_sweet_spot: boolean;
    is_speculative: boolean;
}

export interface ConvergenceMeta {
    reference_date: string;
    window_days: number;
    sweet_spot_thresholds: {
        potentiel_pct: number;
        tech_score: number;
    };
}

const DEFAULT_META: ConvergenceMeta = {
    reference_date: '',
    window_days: 21,
    sweet_spot_thresholds: { potentiel_pct: 15, tech_score: 1.5 },
};

export function useConvergenceRadar() {
    const { token } = useAuth();
    const [stocks, setStocks] = useState<ConvergenceStock[]>([]);
    const [meta, setMeta] = useState<ConvergenceMeta>(DEFAULT_META);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/v1/analysis/convergence`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const result = await response.json();
                setStocks(result.data?.stocks || []);
                setMeta(result.meta || DEFAULT_META);
            } else {
                setError('Erreur serveur lors de la récupération des données.');
            }
        } catch (err) {
            console.error('Error fetching convergence radar:', err);
            setError('Erreur réseau. Vérifiez la connexion API.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchData();
        } else {
            // Pas de token (session en cours de restauration ou expirée) :
            // ne pas laisser le spinner tourner indéfiniment.
            setLoading(false);
        }
    }, [token, fetchData]);

    return { stocks, meta, loading, error, refetch: fetchData };
}
